import {ThreeUnitDetail} from '../ThreeUnitDetail/ThreeUnitDetail';
import {Euler, ExtrudeGeometry, Group, Mesh, MeshStandardMaterial, Object3D, Shape, Vector2, Vector3} from 'three';
import {ThreeFacade} from '../ThreeFacade/ThreeFacade';
import {
    ALIGN_BOTTOM,
    ALIGN_CENTER,
    ALIGN_LEFT,
    ALIGN_RIGHT,
    ALIGN_TOP,
    FACADE_OPEN_TYPE_PENAL_VERTICAL_PIVOT,
    FACADE_OPEN_TYPE_VERTICAL_PIVOT,
    FURNITURE_FT_HANDLE,
    FURNITURE_TYPE_NONE, GOOD_TYPE_HANDLE,
    HANDLE_FUNCTIONAL_TYPE_DEFAULT,
    HANDLE_FUNCTIONAL_TYPE_PROFILE, HANDLE_ID_NONE,
    HANDLE_MODEL_TYPE_DEFAULT,
    HANDLE_TYPE_HORIZONTAL,
    HANDLE_TYPE_VERTICAL,
    PRICE_CELL_HANDLE,
    SIDE_TYPE_BOTTOM,
    SIDE_TYPE_TOP
} from '../../../../../../common-code/constants';
import {THandleModelType} from '../../../../../../common-code/types/handle/THandleModelType';
import {ISaveHandleData} from '../../../../../../common-code/interfaces/saveData/ISaveHandleData';
import {IHandleData} from '../../../../../../common-code/interfaces/materials/IHandleData';
import {IHandleModelData} from '../../../../../../common-code/interfaces/materials/IHandleModelData';
import {IHandlePriceParams} from '../../../../../../common-code/interfaces/catalog/IHandlePriceParams';
import {IDetailKitPriceData} from '../../../../../../common-code/interfaces/catalog/IDetailKitPriceData';
import {IFacadeHandleData} from '../../../../../../common-code/interfaces/materials/IFacadeHandleData';
import {IOffer} from '../../../../../../common-code/interfaces/catalog/IOffer';
import {IDetailPriceData} from '../../../../../../common-code/interfaces/catalog/IDetailPriceData';
import {THandleType} from '../../../../../../common-code/types/handle/THandleType';
import {IProjectOfferIds} from '../../../../../../common-code/interfaces/project/IProjectOfferIds';
import {THandleOpenDirection} from '../../../../../../common-code/types/handle/THandleOpenDirection';
import {THandleFunctionalType} from '../../../../../../common-code/types/handle/THandleFunctionalType';
import {TFacadeOpenType} from '../../../../../../common-code/types/TFacadeOpenType';
import {IMaterialData} from '../../../../../../common-code/interfaces/materials/IMaterialData';
import {CommonObjectHelper} from '../../../../helpers/CommonObjectHelper/CommonObjectHelper';
import {TPoint2D} from '../../../../../../common-code/types/TPoint2D';
import {TListBoolean} from '../../../../../../common-code/types/list/TListBoolean';
import {TFacadeSizes} from '../../../../../../common-code/types/TFacadeSizes';
import {THandlePositionInfo} from "../../../../../../common-code/types/handle/THandlePositionInfo";
import {ISettingGroup} from "../../../../../interfaces/settingData/ISettingGroup";
import {IContextIcon} from "../../../../../interfaces/IContextIcon";
import {ACTION_DELETE, ACTION_SETTINGS, SETTING_GROUP_DETAIL} from "../../../../../constants";
import {i18n} from "../../../../../i18n";
import {IAlign} from "../../../../../../common-code/interfaces/geometry/IAlign";
import {IDetailMargin} from "../../../../../../common-code/interfaces/geometry/IDetailMargin";
import {ISettingGroupDetailHandle} from "../../../../../interfaces/settingData/ISettingGroupDetailHandle";

export class ThreeHandle extends ThreeUnitDetail {
    saveData: ISaveHandleData;
    facade: ThreeFacade;
    handleData: IHandleData;
    threeModelData?: IHandleModelData;
    threeModel: Group;
    shape: Shape;
    priceData?: IDetailKitPriceData;
    shapePoints?: Vector2[];

    constructor(options: ISaveHandleData, facade: ThreeFacade) {
        super(options, facade.unit);
        this.facade = facade;
        this.saveData = this.initThreeUnitSaveData(options);
        this.handleData = this.initHandleData();
        this.threeModelData = this.initThreeModelData();
        this.threeModel = new Group();
        this.shape = new Shape();
    }

    public initState(isRebuild?: boolean) {
        this.createCoverPoints();
        this.createThreeModel();
        super.initState(isRebuild);
    }

    public setHandleData(handle?: IHandleData) {
        let handleData: ISaveHandleData | undefined;
        let facadeHandleData: IFacadeHandleData | IHandleData | undefined;
        let newHandle: IHandleData | undefined;

        facadeHandleData = handle && !handle.notPrice ? handle : this.facade.facadeData.handle;
        if (handle) {
            const currentModelType: THandleModelType = this.getModelType();
            if (handle.models.filter(item => (item.type === currentModelType) ||
                (item.type === undefined && currentModelType === HANDLE_MODEL_TYPE_DEFAULT)).length) {
                this.saveData.modelId = handle.id;
            }
        }
        if (this.handleData.notPrice) {
            delete this.saveData.margin;
        }

        handleData = this.facade.calculateHandleData(this.saveData, facadeHandleData);
        if (handleData && handleData.modelId) {
            newHandle = this.service.getHandleData(handleData.modelId);
            if (newHandle) {
                delete handleData.position;
                delete handleData.rotation;
                if (handleData.horizontalMove && this.facade && handleData.align) {
                    if ((this.facade.getOpenType() !== undefined &&
                        [
                            FACADE_OPEN_TYPE_VERTICAL_PIVOT,
                            FACADE_OPEN_TYPE_PENAL_VERTICAL_PIVOT
                        ].includes(this.facade.getOpenType() as TFacadeOpenType)) &&
                        !this.facade.isDisableSideTypes()) {
                        switch (this.facade.getSideType()) {
                            case ALIGN_LEFT:
                                handleData.align.x = ALIGN_RIGHT;
                                break;
                            case ALIGN_RIGHT:
                                handleData.align.x = ALIGN_LEFT;
                                break;
                        }
                    }
                }
                this.saveData = handleData;
                this.handleData = newHandle;

                return true;
            }
        }

        return false;
    }

    public rebuild() {
        this.view3d.remove(this.threeModel);
        this.threeModelData = this.initThreeModelData();
        this.threeModel = new Group();
        this.initState(true);
        this.createView(true);
    }

    public isCalculatePrice(): boolean {
        if (this.facade.facadeData.kitHandle &&
            this.handleData.id === this.facade.facadeData.kitHandle) {
            if (this.getPriceData()) {
                this.setPriceData(undefined);
            }
            return false;
        }
        return this.handleData.notPrice !== undefined ?
            this.handleData.notPrice : true;
    }

    public getPrice() {
        let priceData: IDetailKitPriceData | undefined;

        priceData = this.getPriceData();
        if (priceData) {
            return priceData.price;
        }
        return 0;
    }

    public setPriceData(priceData: IDetailKitPriceData | undefined) {
        this.priceData = priceData;
        if (priceData) {
            this.service.setOrderPartsToDetailPrice(priceData, this.service.getOrderParts());
        }
    }

    public getPriceData(): IDetailKitPriceData | undefined {
        return this.priceData;
    }

    public getOfferIds(): string[] {
        let offerIds: string[] = [];
        let offers: IOffer[];
        let offer: IOffer;
        const externalIdName: 'externalGuid' | 'vendorCode' = this.service.getOfferExternalId();

        offers = this.getOffers();
        for (offer of offers) {
            if (offer.importOffer[externalIdName]) {
                offerIds.push(offer.importOffer[externalIdName]);
            }
        }

        return offerIds;
    }

    public getOfferIdsWithCounts(): IProjectOfferIds {
        let offerIds: IProjectOfferIds = {};
        let offers: IOffer[];
        let offer: IOffer;
        let id: string;

        offers = this.getOffers();
        for (offer of offers) {
            id = offer.importOffer[this.service.getOfferExternalId()];
            if (!offerIds[id]) {
                offerIds[id] = {id: id, count: offer.count};
            } else {
                offerIds[id].count += offer.count;
            }
        }

        return offerIds;
    }

    public getOffers(): IOffer[] {
        let offers: IOffer[] = [];
        let itemPriceData: IDetailPriceData;

        if (this.priceData && this.priceData.kit) {
            for (itemPriceData of this.priceData.kit) {
                if (itemPriceData.offer && itemPriceData.count > 0) {
                    offers.push({
                        importOffer: itemPriceData.offer,
                        price: itemPriceData.price,
                        oldPrice: itemPriceData.oldPrice,
                        stock: itemPriceData.stock,
                        active: itemPriceData.active,
                        count: itemPriceData.count,
                        part: itemPriceData.part,
                    });
                }
            }
        }

        return offers;
    }

    public isIntegration(): boolean {
        return false;
    }

    public getPriceParams(): IHandlePriceParams | undefined {
        if (!this.saveData.calculate) {
            return undefined;
        }
        debugger;
        return {
            functionalType: FURNITURE_FT_HANDLE,
            catalogCode: 'handle',
            depth: this.getDepth(),
            width: this.getWidth(),
            height: this.getHeight(),
            furnitureType: this.saveData.furnitureType || FURNITURE_TYPE_NONE,
            count: 1,
            part: this.getOrderPart(),
            unitId: this.unit.getId(),
            cell: PRICE_CELL_HANDLE,
        };
    }

    public getHandleDataOfferId(): string | undefined {
        return this.handleData.vendorCode || this.handleData.externalGuid;
    }

    public setLoadModel(type: string, details: Object3D[]) {
        let detail: Object3D;
        let newDetail: Object3D;

        for (detail of details) {
            newDetail = detail.clone();
            newDetail.matrixAutoUpdate = false;
            this.threeModel.add(newDetail);
        }
        this.updateViewType();
        this.afterCreateThreeModel();
    }

    public getWidth(): number {
        switch (this.getFunctionalType()) {
            case HANDLE_FUNCTIONAL_TYPE_PROFILE:
                return this.facade.getWidth();
            case HANDLE_FUNCTIONAL_TYPE_DEFAULT:
            default:
                return this.threeModelData ?
                    +this.threeModelData.width :
                    (this.saveData.sizes ? this.saveData.sizes.width : 0);
        }
    }

    public getDepth(): number {
        switch (this.getFunctionalType()) {
            case HANDLE_FUNCTIONAL_TYPE_PROFILE:
                return this.saveData.profileSizes?.width || 0;
            case HANDLE_FUNCTIONAL_TYPE_DEFAULT:
            default:
                return this.threeModelData ?
                    +this.threeModelData.depth :
                    (this.saveData.sizes ? this.saveData.sizes.depth : 0);
        }
    }

    public getHeight(): number {
        switch (this.getFunctionalType()) {
            case HANDLE_FUNCTIONAL_TYPE_PROFILE:
                return this.saveData.profileSizes?.height || 0;
            case HANDLE_FUNCTIONAL_TYPE_DEFAULT:
            default:
                return this.threeModelData ?
                    +this.threeModelData.height :
                    (this.saveData.sizes ? this.saveData.sizes.height : 0);
        }
    }

    public getOrderPart(): number | undefined {
        if (!this.service.getCanOrderPart()) {
            return undefined;
        }

        return 1;
    }

    public getModelType(): THandleModelType {
        if (!this.saveData.modelType) {
            this.saveData.modelType = HANDLE_MODEL_TYPE_DEFAULT;
        }
        const facadeSizes: TFacadeSizes = this.facade.getSizes();
        const sizeId: string = facadeSizes.width + '';
        if (this.saveData.widthCalculateData &&
            this.saveData.widthCalculateData[sizeId] &&
            this.saveData.widthCalculateData[sizeId].modelType) {
            return this.saveData.widthCalculateData[sizeId].modelType as THandleModelType;
        }

        return this.saveData.modelType;
    }

    public getData(): ISaveHandleData {
        let data: ISaveHandleData = super.getData();
        data.modelId = this.getModelId();
        if (this.view3d.userData.basePosition && this.view3d.userData.basePosition.isVector3) {
            data.position = {
                x: this.view3d.userData.basePosition.x,
                y: this.view3d.userData.basePosition.y,
                z: this.view3d.userData.basePosition.z
            }
        }

        return data;
    }

    public getModelId(): string {
        return this.handleData.id;
    }

    public getType(): THandleType {
        if (this.saveData.hasCustomPosition &&
            this.saveData.customPosition &&
            this.saveData.customPosition.location) {
            return this.saveData.customPosition.location;
        }
        return this.saveData.location || HANDLE_TYPE_HORIZONTAL;
    }

    public getOpenDirection(): THandleOpenDirection {
        return this.saveData.openDirection !== undefined ? this.saveData.openDirection : SIDE_TYPE_TOP;
    }

    public getFunctionalType(): THandleFunctionalType {
        return this.saveData.functionalType || HANDLE_FUNCTIONAL_TYPE_DEFAULT;
    }

    public isUniquePieces(): boolean {
        return this.saveData.isUniquePieces === true;
    }

    public hasCustomPosition(): boolean {
        return this.saveData.hasCustomPosition || false;
    }

    public isVisible(): boolean {
        if (this.isIntegration()) {
            return this.facade.isIntegrationHandle();
        } else {
            return this.view3d.visible;
        }
    }

    public getCustomPositionSettings(): THandlePositionInfo {
        if (this.saveData.customPosition) {
            return this.saveData.customPosition;
        }

        return this.facade.getDefaultHandleCustomPositionSettings();
    }


    public getAutoPositionSettings(): THandlePositionInfo {
        return {
            align: this.saveData.align || {x: ALIGN_CENTER, y: ALIGN_TOP},
            location: this.saveData.location || HANDLE_TYPE_HORIZONTAL,
            margin: this.saveData.margin || {x: 0, y: 0, z: 0}
        }
    }

    public getSettingsGroups(): {[key: string]: ISettingGroup} {
        let groups: {[key: string]: ISettingGroup};

        groups = {
            [SETTING_GROUP_DETAIL]: {
                id: SETTING_GROUP_DETAIL,
                title: 'Свойства ручки',
                sort: 0,
                accordionActive: true,
                data: {
                    type: GOOD_TYPE_HANDLE,
                    unitId: this.unit.getId(),
                    detailId: this.getId(),
                    handleData: this.handleData,
                    facadeData: this.facade.getData(),
                    hasCustomPosition: this.hasCustomPosition(),
                    customPosition: this.getCustomPositionSettings(),
                    autoPosition: this.getAutoPositionSettings(),
                    level: this.facade.getLevel(),
                    facadeMaterial: this.facade.getFacadeMaterial(),
                    isDelete: this.handleData.id === HANDLE_ID_NONE,
                } as ISettingGroupDetailHandle
            } as ISettingGroup
        };

        return groups;
    }

    public getContextIcons(): IContextIcon[] {
        let icons: IContextIcon[];
        let actionData = this.actionData();

        icons = [
            {
                channelName: 'ThreeHandle',
                action: ACTION_SETTINGS,
                actionData: actionData,
                popup: true,
                icon: 'newSettings',
                hide: false,
                title: i18n.t('Свойства'),
                sort: 0,
            },
            {
                channelName: 'ThreeHandle',
                action: ACTION_DELETE,
                actionData: actionData,
                popup: false,
                icon: 'newDelete',
                hide: true,
                title: i18n.t('Удалить'),
                sort: 1,
            }
        ];

        return icons;
    }

    protected setView3dName() {
        this.view3d.name = 'ThreeHandle';
    }

    protected createCoverPoints() {
        let coverPoints: Vector3[];

        coverPoints = [
            new Vector3(-this.getWidth()/2, -this.getHeight()/2, 0),
            new Vector3(this.getWidth()/2, this.getHeight()/2, this.getDepth()),
        ];
        this.addCoverPoints(coverPoints);
    }
    protected createSelectCover(isRebuild?: boolean) {
        super.createSelectCover(isRebuild);
        this.selectCover.userData.commonObject = this;
        this.selectCover.name = 'HandleSelectCover';
    }

    protected initThreeUnitSaveData(saveData: ISaveHandleData): ISaveHandleData {
        super.initThreeUnitSaveData(saveData);

        if (!saveData.align) {
            saveData.align = {x: ALIGN_CENTER, y: ALIGN_TOP};
        }
        if (!saveData.location) {
            saveData.location = HANDLE_TYPE_VERTICAL;
        }
        if (saveData.isDimensions === undefined) {
            saveData.isDimensions = false;
        }
        if (saveData.horizontalMove === undefined) {
            saveData.horizontalMove = false;
        }
        if (!saveData.modelType) {
            saveData.modelType = HANDLE_MODEL_TYPE_DEFAULT;
        }
        if (saveData.horizontalMove && this.facade) {
            if ((this.facade.getOpenType() !== undefined &&
                [
                    FACADE_OPEN_TYPE_VERTICAL_PIVOT,
                    FACADE_OPEN_TYPE_PENAL_VERTICAL_PIVOT
                ].includes(this.facade.getOpenType() as TFacadeOpenType)) &&
                !this.facade.isDisableSideTypes()) {
                switch (this.facade.getSideType()) {
                    case ALIGN_LEFT:
                        saveData.align.x = ALIGN_RIGHT;
                        break;
                    case ALIGN_RIGHT:
                        saveData.align.x = ALIGN_LEFT;
                        break;
                }
            }
        }

        return saveData;
    }

    protected initHandleData(): IHandleData {
        let handleData: IHandleData | undefined;

        if (this.saveData.modelId) {
            handleData = this.service.getHandleData(this.saveData.modelId);
        }
        if (!handleData) {
            return this.service.getDefaultHandleData(this.unit.getLevel());
        } else {
            return handleData;
        }
    }

    protected initThreeModelData(): IHandleModelData | undefined {
        return this.service.getHandleThreeModel(this.handleData, this.getModelType());
    }

    protected createThreeModel() {
        this.threeModel.name = 'threeModel';
        this.threeModel.matrixAutoUpdate = false;
        this.view3d.add(this.threeModel);
        switch (this.getFunctionalType()) {
            case HANDLE_FUNCTIONAL_TYPE_PROFILE:
                this.createProfileHandle();
                break;
            case HANDLE_FUNCTIONAL_TYPE_DEFAULT:
            default:
                this.service.loadHandleThreeModel(this);
        }
    }

    protected createProfileHandle() {
        this.createShape();
        this.createProfileBody();
        this.afterCreateThreeModel();
    }

    protected createProfileBody(): void {
        let geometry;
        let extrudeSettings;
        let profileBody: Mesh;

        extrudeSettings = {
            steps: 1,
            depth: this.facade.getWidth(),
            bevelEnabled: false
        };
        geometry = new ExtrudeGeometry(this.shape, extrudeSettings);
        geometry.center();
        profileBody = new Mesh(geometry, this.getProfileBodyMaterial());
        profileBody.rotation.y = -0.5 * Math.PI;
        profileBody.updateMatrix();
        profileBody.geometry.applyMatrix4(profileBody.matrix);
        profileBody.rotation.y = 0;
        profileBody.name = "profileBody";
        profileBody.matrixAutoUpdate = false;
        profileBody.updateMatrix();
        this.threeModel.add(profileBody);
    }

    protected afterCreateThreeModel() {
        this.threeModel.castShadow = true;
        this.threeModel.traverse((child) => {
            if ('isMesh' in child &&
                child instanceof Mesh) {
                child.castShadow = true;
            }
        })
    }

    protected getProfileBodyMaterial() {
        let material: IMaterialData | undefined;
        if (this.saveData.profileMaterial) {
            material = this.service.getMaterialById(this.saveData.profileMaterial);
        }
        return new MeshStandardMaterial(CommonObjectHelper.getMeshStandardMaterialParameters({
            color: material?.color || "#cccccc",
            emissive: material?.emissiveColor || undefined,
            map: null,
            normalMap: null,
            roughnessMap: null,
            metalness: 0.1,
            roughness: 0.1,
        }));
    }

    protected createShape(shapePoints?: Vector2[]) {
        if (!shapePoints) {
            shapePoints = this.calculateShapePoints();
        }
        this.shapePoints = shapePoints;
        this.shape.setFromPoints(this.shapePoints);
    }

    protected calculateShapePoints(): Vector2[] {
        let contourPath: TPoint2D[] | undefined;
        let uniquePoints: TListBoolean = {};
        let shapePoints: Vector2[] = [];
        let index: number;

        contourPath = this.getContourPath();

        for (index = 0; index < contourPath.length; index++) {
            if (contourPath[index] === undefined) {
                continue;
            }
            if (!uniquePoints[contourPath[index].x + '_' + contourPath[index].y]) {
                shapePoints.push(new Vector2(contourPath[index].x, contourPath[index].y));
                uniquePoints[contourPath[index].x + '_' + contourPath[index].y] = true;
            }
        }
        shapePoints.push(new Vector2(contourPath[0].x, contourPath[0].y));

        return shapePoints;
    }

    protected getContourPath(): TPoint2D[] {
        let contourPath: TPoint2D[] | undefined;

        contourPath = this.saveData.contourPath;
        if (contourPath) {
            return contourPath;
        } else {
            return this.defaultContourPath();
        }
    }

    protected defaultContourPath(): TPoint2D[] {
        return [
            {x: 0, y: 0},
            {x: 0, y: this.getHeight()},
            {x: this.getDepth(), y: this.getHeight()},
            {x: this.getDepth(), y: 0},
            {x: 0, y: 0},
        ];
    }

    protected initPosition(): Vector3 {
        let position: Vector3;
        let align: IAlign | undefined;
        let margin: IDetailMargin | undefined;
        let location: THandleType;

        position = new Vector3(0, 0, this.facade.getDepth());
        if (this.saveData.hasCustomPosition) {
            const customPosition: THandlePositionInfo =
                this.saveData.customPosition || this.facade.getDefaultHandleCustomPositionSettings();
            align = customPosition.align;
            margin = customPosition.margin;
            location = customPosition.location;
        } else {
            align = this.saveData.align;
            margin = this.saveData.margin;
            location = this.saveData.location || HANDLE_TYPE_HORIZONTAL;
        }
        if (align) {
            switch (align.x) {
                case ALIGN_LEFT:
                    position.x = location === HANDLE_TYPE_HORIZONTAL ?
                        -this.facade.getWidth() / 2 + this.getWidth() / 2 : -this.facade.getWidth() / 2 + this.getDepth() / 2;
                    position.x += margin && margin.x ? +margin.x : 0;
                    break;
                case ALIGN_RIGHT:
                    position.x = location === HANDLE_TYPE_HORIZONTAL ?
                        this.facade.getWidth() / 2 - this.getWidth() / 2 : this.facade.getWidth() / 2 - this.getDepth() / 2;
                    position.x -= margin && margin.x ? +margin.x : 0;
                    break;
                case ALIGN_CENTER:
                    position.x += margin && margin.x ? +margin.x : 0;
                    break;
            }
            switch (align.y) {
                case ALIGN_BOTTOM:
                    position.y = location === HANDLE_TYPE_HORIZONTAL ?
                        -this.facade.getHeight() / 2 + this.getHeight() / 2 : -this.facade.getHeight() / 2 + this.getWidth() / 2;
                    position.y += margin && margin.y ? +margin.y : 0;
                    break;
                case ALIGN_TOP:
                    position.y = location === HANDLE_TYPE_HORIZONTAL ?
                        this.facade.getHeight() / 2 - this.getHeight() / 2 : this.facade.getHeight() / 2 - this.getWidth() / 2;
                    position.y -= margin && margin.y ? +margin.y : 0;
                    break;
                case ALIGN_CENTER:
                    position.y -= margin && margin.y ? +margin.y : 0;
                    break;
            }
            position.z = this.facade.getDepth();
            position.z += margin && margin.z ? +margin.z : 0;
        }
        if (this.facade.isFlipY()) {
            position.x = -position.x;
            position.y = -position.y;
        }

        return position;
    }

    protected initRotation(): Euler {
        let rotation: Euler;
        let align: IAlign | undefined;
        let location: THandleType;

        rotation = new Euler();
        if (this.saveData.hasCustomPosition) {
            const customPosition: THandlePositionInfo =
                this.saveData.customPosition || this.facade.getDefaultHandleCustomPositionSettings();
            align = customPosition.align;
            location = customPosition.location;
        } else {
            align = this.saveData.align;
            location = this.saveData.location || HANDLE_TYPE_HORIZONTAL;
        }
        if (align) {
            switch (align.y) {
                case ALIGN_BOTTOM:
                    rotation.z = location === HANDLE_TYPE_HORIZONTAL ? Math.PI : -Math.PI;
                    if (this.facade.isFlipY()) {
                        rotation.z += Math.PI;
                    }
                    break;
            }
            switch (align.x) {
                case ALIGN_LEFT:
                    switch (this.getOpenDirection()) {
                        case SIDE_TYPE_TOP:
                            rotation.z += location === HANDLE_TYPE_VERTICAL ? -Math.PI * 0.5 : 0;
                            break;
                        case SIDE_TYPE_BOTTOM:
                            rotation.z += location === HANDLE_TYPE_VERTICAL ? Math.PI * 0.5 : 0;
                            break;
                    }
                    break;
                case ALIGN_RIGHT:
                    switch (this.getOpenDirection()) {
                        case SIDE_TYPE_TOP:
                            rotation.z += location === HANDLE_TYPE_VERTICAL ? Math.PI * 0.5 : 0;
                            break;
                        case SIDE_TYPE_BOTTOM:
                            rotation.z += location === HANDLE_TYPE_VERTICAL ? -Math.PI * 0.5 : 0;
                            break;
                    }
                    break;
                default:
                    rotation.z += location === HANDLE_TYPE_VERTICAL ? -Math.PI * 0.5 : 0;
                    break;
            }
        }

        return rotation;
    }

    protected addToScene() {
        this.view3d.name = 'ThreeHandle';
        this.unit.view3d.add(this.view3d);
    }
}